import { useEffect, useRef, useState, useCallback } from 'react';

const AUDIO_TYPE = 'audio/mpeg';

const setupMediaSource = async (
	audio: HTMLAudioElement,
): Promise<{
	mediaSource: MediaSource;
	sourceBuffer: SourceBuffer;
}> => {
	const mediaSource = new MediaSource();

	await new Promise<void>((resolve, reject) => {
		const handleSourceOpen = () => {
			mediaSource.removeEventListener('sourceopen', handleSourceOpen);
			mediaSource.removeEventListener('error', handleError);
			resolve();
		};

		const handleError = () => {
			mediaSource.removeEventListener('sourceopen', handleSourceOpen);
			mediaSource.removeEventListener('error', handleError);
			reject(new Error('MediaSource error'));
		};

		mediaSource.addEventListener('sourceopen', handleSourceOpen);
		mediaSource.addEventListener('error', handleError);
		audio.src = URL.createObjectURL(mediaSource);
		audio.load();
	});

	const sourceBuffer = mediaSource.addSourceBuffer(AUDIO_TYPE);
	sourceBuffer.mode = 'sequence';

	return { mediaSource, sourceBuffer };
};

const cleanupMediaSource = (mediaSource: MediaSource | null): void => {
	if (mediaSource?.readyState === 'open') {
		try {
			mediaSource.endOfStream();
		} catch (e) {
			// Ignore errors during cleanup but we don't actually want to propagate them
		}
	}
};

const createNewMediaSource = async (
	audio: HTMLAudioElement,
	onSuccess: (mediaSource: MediaSource, sourceBuffer: SourceBuffer) => void,
	onError: (error: Error) => void,
): Promise<void> => {
	try {
		const { mediaSource, sourceBuffer } = await setupMediaSource(audio);
		onSuccess(mediaSource, sourceBuffer);
	} catch (e) {
		onError(e as Error);
	}
};

const appendChunk = async (
	sourceBuffer: SourceBuffer,
	chunk: Uint8Array,
	isFirstChunk: boolean,
	audio: HTMLAudioElement | null,
	onError: (error: Error) => void,
) => {
	return new Promise<void>((resolve, reject) => {
		if (sourceBuffer.updating) {
			sourceBuffer.addEventListener(
				'updateend',
				() => {
					appendChunk(sourceBuffer, chunk, isFirstChunk, audio, onError)
						.then(resolve)
						.catch(reject);
				},
				{ once: true },
			);
			return;
		}

		sourceBuffer.appendBuffer(chunk);
		sourceBuffer.addEventListener(
			'updateend',
			() => {
				if (isFirstChunk && audio) {
					// TODO: Update isFirstChunk logic to use a ref
					// as this block of code may be executing more than expected
					isFirstChunk = false;
				}
				resolve();
			},
			{ once: true },
		);
	});
};

export const useAudioStream = (
	stream: ReadableStream<Uint8Array> | undefined,
	onError: (error: Error) => void,
	setStreamEnded?: (streamEnded: boolean) => void,
) => {
	const audioRef = useRef<HTMLAudioElement>(null);
	const mediaSourceRef = useRef<MediaSource | null>(null);
	const sourceBufferRef = useRef<SourceBuffer | null>(null);
	const [isAudioMounted, setIsAudioMounted] = useState(false);
	const [isMediaSourceReady, setIsMediaSourceReady] = useState(false);
	const streamRef = useRef<ReadableStream<Uint8Array> | undefined>(stream);

	const handleStreamChange = useCallback(
		(newStream: ReadableStream<Uint8Array> | undefined) => {
			if (!audioRef.current) {
				return;
			}

			// Update the stream reference
			streamRef.current = newStream;

			// Clean up existing MediaSource
			cleanupMediaSource(mediaSourceRef.current);

			// Reset state
			setIsMediaSourceReady(false);
			mediaSourceRef.current = null;
			sourceBufferRef.current = null;

			// Setup new MediaSource if we have a stream
			if (newStream) {
				void createNewMediaSource(
					audioRef.current,
					(mediaSource, sourceBuffer) => {
						mediaSourceRef.current = mediaSource;
						sourceBufferRef.current = sourceBuffer;
						setIsMediaSourceReady(true);
					},
					onError,
				);
			}
		},
		[onError],
	);

	// Detect when audio element is mounted
	useEffect(() => {
		const observer = new MutationObserver((mutations) => {
			mutations.forEach((mutation) => {
				if (mutation.type === 'childList') {
					mutation.addedNodes.forEach((node) => {
						if (node === audioRef.current) {
							setIsAudioMounted(true);
							observer.disconnect();
						}
					});
				}
			});
		});

		observer.observe(document.body, { childList: true, subtree: true });

		return () => {
			observer.disconnect();
		};
	}, []);

	// Check if stream changed and setup a new MediaSource if needed
	useEffect(() => {
		if (stream !== streamRef.current) {
			handleStreamChange(stream);
		}
	}, [stream, onError, handleStreamChange]);

	// Setup MediaSource
	useEffect(() => {
		const setupAudio = async () => {
			const audio = audioRef.current;
			if (!audio) {
				return;
			}

			try {
				const { mediaSource, sourceBuffer } = await setupMediaSource(audio);
				mediaSourceRef.current = mediaSource;
				sourceBufferRef.current = sourceBuffer;
				setIsMediaSourceReady(true);
			} catch (e) {
				onError(e as Error);
			}
		};

		setupAudio().catch(onError);

		return () => {
			cleanupMediaSource(mediaSourceRef.current);
			setIsMediaSourceReady(false);
			mediaSourceRef.current = null;
			sourceBufferRef.current = null;
		};

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isAudioMounted]);

	// Handle streaming
	useEffect(() => {
		let isMounted = true;

		const handleStream = async () => {
			try {
				if (!stream || !sourceBufferRef.current) {
					return;
				}

				const reader = stream.getReader();
				const isFirstChunk = true;

				while (isMounted) {
					const { done, value } = await reader.read();

					if (done) {
						setStreamEnded && setStreamEnded(true);
						break;
					}

					if (!isMounted) {
						break;
					}

					if (!sourceBufferRef.current) {
						break;
					}

					await appendChunk(
						sourceBufferRef.current,
						value,
						isFirstChunk,
						audioRef.current,
						onError,
					);
				}

				if (isMounted && mediaSourceRef.current?.readyState === 'open') {
					mediaSourceRef.current.endOfStream();
				}
			} catch (e) {
				if (isMounted) {
					onError(e as Error);
				}
			}
		};

		if (stream && isMediaSourceReady && sourceBufferRef.current) {
			void handleStream();
		}

		return () => {
			isMounted = false;
		};
	}, [stream, onError, isMediaSourceReady, setStreamEnded]);

	return { audioRef };
};
